import { memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SVGIconType } from '../../../../constants/svgIcon';
import {
  getWebPageVariant,
  WebPageType,
} from '../../../../constants/webPageType';
import {
  CountDownText,
  LoadingSpinner,
  SmsCodeInput,
  SvgIcon,
  TextButton,
  TextButtonSize,
} from '../../../Molecules';

import styles from './PhoneVerificationTemplate.module.css';

type PhoneVerificationTemplateProps = {
  phoneNumber: string;
  resendCountDown: number;
  errorMessage: string;
  isLoading: boolean;
  onClickConfirm: (code: string) => void;
  onClickResend: () => void;
  onClickBack: () => void;
  webPageType: WebPageType;
};

export const PhoneVerificationTemplate = memo(
  ({
    phoneNumber,
    resendCountDown,
    errorMessage,
    onClickConfirm,
    onClickResend,
    onClickBack,
    isLoading,
    webPageType,
  }: PhoneVerificationTemplateProps) => {
    const { t } = useTranslation();

    const [code, setCode] = useState<string[]>(Array(6).fill(''));
    const [disabled, setDisabled] = useState(Date.now() < resendCountDown);
    const [codeErrorMessage, setCodeErrorMessage] = useState(errorMessage);

    useEffect(() => {
      if (Date.now() < resendCountDown) {
        setDisabled(true);
      }
    }, [resendCountDown]);

    useEffect(() => {
      if (errorMessage) {
        setCodeErrorMessage(errorMessage);
      }
    }, [errorMessage]);

    const handleConfirmClick = useCallback(() => {
      onClickConfirm(code.join(''));
    }, [onClickConfirm, code]);

    return (
      <div className={styles.phoneVerify}>
        <div className={styles.centeredContainer}>
          <button className={styles.backButton} onClick={onClickBack}>
            <SvgIcon
              name={SVGIconType.Arrow}
              color="black"
              width={24}
              height={24}
            />
          </button>
          <p
            className={`${styles.title} ${
              styles[getWebPageVariant(webPageType).classVariantLight2]
            }`}
          >
            {t('Verify Your Phone')}
          </p>
          <p className={styles.text}>
            {t(
              'We have just sent you a verification message to {phoneNumber}. Please enter the 6-digit code from your SMS message.',
              { phoneNumber },
            )}
          </p>
          <SmsCodeInput
            code={code}
            isError={!!codeErrorMessage}
            onCodeChange={(codes: string[]) => {
              setCode(codes);
              setCodeErrorMessage('');
            }}
          />
          <div className={styles.warning}>{t(codeErrorMessage)}</div>
          <TextButton
            text={t('Confirm')}
            size={TextButtonSize.Medium}
            variant={getWebPageVariant(webPageType).textButtonVariant}
            onClick={handleConfirmClick}
            disabled={code.some((c) => c === '')}
          />

          <div
            className={`${styles.resendButton} ${
              disabled ? styles.disabled : ''
            } ${styles[getWebPageVariant(webPageType).classVariantBackground]}`}
            onClick={disabled ? undefined : onClickResend}
          >
            {disabled ? (
              <CountDownText
                timestamp={resendCountDown}
                label={t('Resend')}
                className={styles.resendText}
                timeStringClassName={styles.resendTextCountdown}
                onTimeout={() => {
                  // Enable the button when the time is up
                  setDisabled(false);
                }}
              />
            ) : (
              <p className={styles.resendText}>{t('Resend')}</p>
            )}
            {disabled ? <div className={styles.blackShadow} /> : null}
          </div>
        </div>
        {isLoading && <LoadingSpinner loading={isLoading} />}
      </div>
    );
  },
);
